'use client';

import { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import { search, initializeSearch } from '@/utils/search.js';
import searchIconGray from '/public/icons/searchIconGray.png';

const SearchBar = ({ variant = 'desktop', showSearch, setShowSearch }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const inputRef = useRef(null);

  // Initialize search on component mount
  useEffect(() => {
    const init = async () => {
      const success = await initializeSearch();
      setIsInitialized(success);
    };
    init();
  }, []);

  // Auto-focus input when search bar is shown
  useEffect(() => {
    if ((variant === 'desktop' && showSearch) || variant === 'mobile') {
      // Small delay to ensure the input is rendered
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [showSearch, variant]);

  const handleSearch = async (e) => {
    const value = e.target.value;
    setQuery(value);
    if (value && isInitialized) {
      const searchResults = search(value, 8); // Use the new search function
      setResults(searchResults);
    } else {
      setResults([]);
    }
  };

  const handleClear = () => {
    setQuery('');
    setResults([]);
  };

  const handleClose = () => {
    setShowSearch(false);
    setQuery('');
    setResults([]);
  };

  return (
    <div className="relative">
      {/* Input Field */}
      {variant === 'mobile' || showSearch ? (
        <div
          className={`flex items-center rounded-full border border-gray-300 bg-white shadow-sm ${
            variant === 'desktop' ? 'w-100 rounded-full px-3' : 'rounded-md p-2'
          }`}
        >
          <label htmlFor="search-bar" className="sr-only">
            Search
          </label>
          <Image src={searchIconGray} alt="" width={18} height={18} className="mr-2" />
          <input
            ref={inputRef}
            id="search-bar"
            type="text"
            placeholder="Search"
            value={query}
            onChange={handleSearch}
            className="w-full justify-center bg-transparent text-sm text-black placeholder-gray-500 focus:outline-none"
            aria-label="Search input"
          />
          {variant === 'mobile' && query && (
            <button
              className="ml-2 self-center text-lg font-semibold text-gray-500 hover:text-red-600"
              onClick={handleClear}
              aria-label="Clear search input"
            >
              ×
            </button>
          )}
          {showSearch && (
            <button
              className="w-10 cursor-pointer self-center text-xl font-semibold text-gray-500 hover:text-red-600"
              onClick={handleClose}
              aria-label="Close search bar"
            >
              ×
            </button>
          )}
        </div>
      ) : null}

      {/* Live results */}
      {query && (
        <div
          className={`${
            variant === 'desktop' ? 'absolute top-10 right-0 w-100' : 'relative mt-2 w-full pb-4'
          } z-50 max-h-100 overflow-y-auto rounded-md bg-white p-4 text-black shadow-lg`}
          role="listbox"
          aria-label="Search results"
        >
          {results.length > 0 ? (
            <ul className="space-y-2 text-sm">
              {results.map((item, idx) => (
                <li
                  key={idx}
                  className="border-b pb-1 last:border-0"
                  aria-label={`Result: ${item.title}`}
                >
                  <a href={item.url} className="block text-wrap hover:underline">
                    <div className="mb-1 text-sm font-semibold">{item.title}</div>
                    {item.section && (
                      <div className="mb-1 text-xs font-medium text-blue-600">{item.section}</div>
                    )}
                    {item.snippet && (
                      <div
                        className="mb-1 text-xs text-gray-600"
                        dangerouslySetInnerHTML={{ __html: item.snippet }}
                      />
                    )}
                    <div className="text-xs text-gray-500">{item.type}</div>
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500">No results found</p>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
